import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Save, Package, ClipboardList, ImagePlus, Link2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/data/products";

const STATUSES = ["initiated", "confirmed", "shipped", "delivered", "cancelled"];

const inputCls =
  "w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-brandink outline-none transition-colors focus:border-link";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("cambroo_admin_token") || "");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [edits, setEdits] = useState({});
  const [newProduct, setNewProduct] = useState({ name: "", price: "", category: "headphones-earphones", subcategory: "", image: "", desc: "" });
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [adminVisible, setAdminVisible] = useState(50);
  const [heroFor, setHeroFor] = useState(null);
  const [tileFor, setTileFor] = useState(null);
  const [homeSlides, setHomeSlides] = useState([]);
  const [homeHeroPick, setHomeHeroPick] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [catHeros, setCatHeros] = useState({});
  const [catTiles, setCatTiles] = useState({});
  const [galleryFor, setGalleryFor] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const fileRef = useRef(null);

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [p, o] = await Promise.all([
        api.get("/products"),
        api.get("/admin/orders", authH),
      ]);
      setProducts(p.products);
      setOrders(o.orders);
      setCatHeros(Object.fromEntries((p.categories || []).map((c) => [c.id, c.custom_heroes || []])));
      setCatTiles(Object.fromEntries((p.categories || []).filter((c) => c.image).map((c) => [c.id, c.image])));
      setHomeSlides(p.custom_hero_slides || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("cambroo_admin_token");
        setToken("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setAuthError("");
    setBusy(true);
    try {
      const data = await api.post("/admin/login", { password });
      localStorage.setItem("cambroo_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setAuthError(typeof d === "string" ? d : "Login failed");
    }
    setBusy(false);
  };

  const logout = () => {
    localStorage.removeItem("cambroo_admin_token");
    setToken("");
  };

  const uploadPhoto = async (file) => {
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const d = await api.upload("/admin/upload", fd, authH);
      if (heroFor) {
        const next = [...(catHeros[heroFor] || []), d.url].slice(0, 3);
        await api.put(`/admin/categories/${heroFor}`, { hero_images: next }, authH);
        setCatHeros((m) => ({ ...m, [heroFor]: next }));
        toast.success("Category hero banner added");
        setHeroFor(null);
      } else if (homeHeroPick) {
        const next = [...homeSlides, d.url].slice(0, 3);
        await api.put("/admin/hero", { slides: next }, authH);
        setHomeSlides(next);
        toast.success("Homepage banner added");
        setHomeHeroPick(false);
      } else if (tileFor) {
        await api.put(`/admin/categories/${tileFor}`, { image: d.url }, authH);
        setCatTiles((m) => ({ ...m, [tileFor]: d.url }));
        toast.success("Category photo updated");
        setTileFor(null);
      } else if (galleryFor) {
        setGalleryImages((imgs) => (imgs.length >= 10 ? imgs : [...imgs, d.url]));
        toast.success("Photo added");
      } else {
        setNewProduct((p) => {
          const photos = [...(p.photos || []), d.url].slice(0, 10);
          return { ...p, photos, image: photos[0] || "" };
        });
        toast.success("Photo uploaded");
      }
    } catch (e) {
      toast.error("Photo upload failed");
    }
    setPhotoBusy(false);
  };

  const openGallery = (p) => {
    setHeroFor(null);
    setTileFor(null);
    setHomeHeroPick(false);
    setGalleryFor(p);
    setGalleryImages(p.images?.length ? [...p.images] : p.image ? [p.image] : []);
  };

  const importListing = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const d = await api.post("/admin/fetch-listing", { url: importUrl.trim() }, authH, 150000);
      const photos = [...new Set((d.images || []).filter((u) => typeof u === "string" && u.startsWith("http")))].slice(0, 10);
      setNewProduct((p) => ({
        ...p,
        name: d.name || p.name,
        price: d.price != null ? String(d.price) : p.price,
        desc: d.desc || p.desc,
        photos,
      }));
      setImportUrl("");
      if (!photos.length) {
        toast.warning("Listing loaded, but no photos were found on that page — add photos manually below.");
      } else {
        toast.success(`Listing loaded with ${photos.length} photo${photos.length > 1 ? "s" : ""} — pick the category, review, then tap Add`);
      }
    } catch (e) {
      const status = e.response?.status;
      toast.error(
        status === 502
          ? "Amazon is busy right now — wait a minute and tap Fetch again"
          : e.response?.data?.detail || "Could not fetch that listing — wait a minute and try again"
      );
    } finally {
      setImporting(false);
    }
  };

  const saveGallery = async () => {
    if (!galleryFor) return;
    try {
      await api.put(
        `/admin/products/${galleryFor.id}`,
        { ...galleryFor, images: galleryImages, image: galleryImages[0] || galleryFor.image },
        authH
      );
      toast.success(`${galleryFor.name} photos saved`);
      setGalleryFor(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const removeCatHero = async (cid, url) => {
    const next = (catHeros[cid] || []).filter((u) => u !== url);
    if (!next.length) {
      toast.error("Keep at least one banner");
      return;
    }
    try {
      await api.put(`/admin/categories/${cid}`, { hero_images: next }, authH);
      setCatHeros((m) => ({ ...m, [cid]: next }));
      toast.success("Banner removed");
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  const removeHomeSlide = async (url) => {
    const next = homeSlides.filter((u) => u !== url);
    if (!next.length) {
      toast.error("Keep at least one banner");
      return;
    }
    try {
      await api.put("/admin/hero", { slides: next }, authH);
      setHomeSlides(next);
      toast.success("Banner removed");
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  const saveProduct = async (p) => {
    const e = edits[p.id] || {};
    const payload = { ...p, name: e.name ?? p.name, price: parseFloat(e.price ?? p.price) };
    try {
      await api.put(`/admin/products/${p.id}`, payload, authH);
      toast.success(`${payload.name} saved`);
      setEdits((prev) => ({ ...prev, [p.id]: {} }));
      load();
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const deleteProduct = async (p) => {
    try {
      await api.delete(`/admin/products/${p.id}`, authH);
      toast.success(`${p.name} deleted`);
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.price) return;
    try {
      await api.post("/admin/products", {
        ...newProduct,
        price: parseFloat(newProduct.price),
        image: (newProduct.photos || [])[0] || newProduct.image || "",
        images: newProduct.photos || [],
      }, authH);
      toast.success(`${newProduct.name} added`);
      setNewProduct({ name: "", price: "", category: "headphones-earphones", subcategory: "", image: "", photos: [], desc: "" });
      load();
    } catch (err) {
      toast.error("Add failed");
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status }, authH);
      toast.success(`${orderId} → ${status}`);
      load();
    } catch (err) {
      toast.error("Status update failed");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page px-4">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-xl border border-line bg-white p-8 shadow-sm"
        >
          <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-md bg-brand text-xl font-extrabold lowercase text-brandink">c</span>
          <h1 className="text-2xl font-extrabold text-brandink">Cambroo Admin</h1>
          <p className="mt-1 text-xs text-muted">Restricted area — authorized staff only.</p>
          <input
            data-testid="admin-password-input"
            type="password"
            className={`${inputCls} mt-6`}
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {authError && (
            <p data-testid="admin-login-error" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-600">{authError}</p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={busy || !password}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-brand py-3 text-sm font-bold text-brandink transition-colors hover:bg-branddark disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-page px-4 py-6 lg:px-8">
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        data-testid="admin-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) uploadPhoto(f);
          e.target.value = "";
        }}
      />
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-line bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand text-lg font-extrabold lowercase text-brandink">c</span>
            <div>
              <h1 className="text-lg font-extrabold text-brandink">Cambroo Admin</h1>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted">Products & Orders</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-tab-products"
              onClick={() => setTab("products")}
              className={`flex items-center gap-2 rounded-md border px-4 py-2 text-xs font-semibold transition-colors ${
                tab === "products" ? "border-brand bg-brand text-brandink" : "border-line bg-white text-muted hover:text-brandink"
              }`}
            >
              <Package className="h-3.5 w-3.5" /> Products ({products.length})
            </button>
            <button
              data-testid="admin-tab-orders"
              onClick={() => setTab("orders")}
              className={`flex items-center gap-2 rounded-md border px-4 py-2 text-xs font-semibold transition-colors ${
                tab === "orders" ? "border-brand bg-brand text-brandink" : "border-line bg-white text-muted hover:text-brandink"
              }`}
            >
              <ClipboardList className="h-3.5 w-3.5" /> Orders ({orders.length})
            </button>
            <button
              data-testid="admin-tab-categories"
              onClick={() => setTab("categories")}
              className={`flex items-center gap-2 rounded-md border px-4 py-2 text-xs font-semibold transition-colors ${
                tab === "categories" ? "border-brand bg-brand text-brandink" : "border-line bg-white text-muted hover:text-brandink"
              }`}
            >
              <ImagePlus className="h-3.5 w-3.5" /> Categories
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-md border border-line bg-white px-4 py-2 text-xs font-semibold text-muted transition-colors hover:text-brandink"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        {photoBusy && (
          <p data-testid="admin-photo-uploading" className="mt-4 flex items-center gap-2 text-xs font-semibold text-link">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading photo…
          </p>
        )}

        {tab === "products" && (
          <div>
            <div className="mt-5 flex flex-col gap-2 rounded-xl border border-line bg-white p-4 shadow-sm sm:flex-row sm:items-center" data-testid="admin-import-box">
              <div className="flex items-center gap-2 text-xs font-bold text-brandink">
                <Link2 className="h-4 w-4 text-link" /> Import from Amazon
              </div>
              <input
                className={`${inputCls} flex-1`}
                placeholder="Paste Amazon product link, e.g. https://www.amazon.ae/dp/…"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                data-testid="admin-import-url"
                dir="ltr"
              />
              <button
                type="button"
                data-testid="admin-import-fetch"
                disabled={importing || !importUrl.trim()}
                onClick={importListing}
                className="flex items-center justify-center gap-2 rounded-md bg-brandink px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {importing ? "Fetching… (can take up to a minute)" : "Fetch listing"}
              </button>
            </div>
            <form
              data-testid="admin-add-product-form"
              onSubmit={addProduct}
              className="mt-5 grid grid-cols-1 gap-3 rounded-xl border border-line bg-white p-5 shadow-sm sm:grid-cols-7"
            >
              <input className={inputCls} placeholder="Product name *" value={newProduct.name}
                onChange={(e) => setNewProduct((p) => ({ ...p, name: e.target.value }))} data-testid="admin-new-name" />
              <input className={inputCls} placeholder="Price AED *" type="number" min="1" step="0.01" value={newProduct.price}
                onChange={(e) => setNewProduct((p) => ({ ...p, price: e.target.value }))} data-testid="admin-new-price" />
              <select className={`${inputCls} cursor-pointer`} value={newProduct.category}
                onChange={(e) => setNewProduct((p) => ({ ...p, category: e.target.value, subcategory: "" }))} data-testid="admin-new-category">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select className={`${inputCls} cursor-pointer`} value={newProduct.subcategory}
                onChange={(e) => setNewProduct((p) => ({ ...p, subcategory: e.target.value }))} data-testid="admin-new-subcategory">
                <option value="">Subcategory</option>
                {(CATEGORIES.find((c) => c.id === newProduct.category)?.subs || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <div className="flex flex-wrap items-center gap-2" data-testid="admin-new-photos">
                {(newProduct.photos || []).map((u, i) => (
                  <span key={i} className="relative">
                    <img src={u} alt="" className="h-10 w-10 rounded-md border border-brand object-cover" />
                    <button
                      type="button"
                      data-testid={`admin-new-photo-remove-${i}`}
                      onClick={() =>
                        setNewProduct((p) => {
                          const photos = (p.photos || []).filter((_, j) => j !== i);
                          return { ...p, photos, image: photos[0] || "" };
                        })
                      }
                      className="absolute -end-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] text-white"
                      aria-label="Remove photo"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {(newProduct.photos || []).length < 10 && (
                  <button
                    type="button"
                    data-testid="admin-new-photo-button"
                    onClick={() => { setGalleryFor(null); setHeroFor(null); fileRef.current?.click(); }}
                    className={`${inputCls} flex items-center justify-center gap-2 text-[11px] font-semibold text-muted hover:border-link hover:text-link`}
                  >
                    <ImagePlus className="h-3.5 w-3.5" />
                    Photos ({(newProduct.photos || []).length}/10)
                  </button>
                )}
              </div>
              <input className={inputCls} placeholder="Short description" value={newProduct.desc}
                onChange={(e) => setNewProduct((p) => ({ ...p, desc: e.target.value }))} data-testid="admin-new-desc" />
              <button data-testid="admin-add-product-button" type="submit"
                className="flex items-center justify-center gap-2 rounded-md bg-brand py-2 text-xs font-bold text-brandink transition-colors hover:bg-branddark">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </form>

            <div className="mt-4 space-y-2" data-testid="admin-products-list">
              {products.slice(0, adminVisible).map((p) => {
                const e = edits[p.id] || {};
                const dirty = e.name !== undefined || e.price !== undefined;
                return (
                  <div key={p.id} data-testid={`admin-product-${p.id}`}
                    className="grid grid-cols-[56px_1fr_110px_130px_auto] items-center gap-3 rounded-lg border border-line bg-white p-3 max-md:grid-cols-1">
                    <div className="relative h-14 w-14 shrink-0">
                      <img src={p.image} alt={p.name} className="h-14 w-14 rounded-md border border-line object-contain" />
                      <button
                        data-testid={`admin-photo-${p.id}`}
                        onClick={() => openGallery(p)}
                        className="absolute inset-0 flex items-center justify-center rounded-md bg-brandink/60 opacity-0 transition-opacity duration-200 hover:opacity-100"
                        aria-label={`Manage photos of ${p.name}`}
                        title="Manage photos (up to 10)"
                      >
                        <ImagePlus className="h-4 w-4 text-brand" />
                      </button>
                    </div>
                    <input className={inputCls} value={e.name ?? p.name}
                      onChange={(ev) => setEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], name: ev.target.value } }))}
                      data-testid={`admin-name-${p.id}`} />
                    <input className={inputCls} type="number" min="1" step="0.01" value={e.price ?? p.price}
                      onChange={(ev) => setEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], price: ev.target.value } }))}
                      data-testid={`admin-price-${p.id}`} />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</span>
                    <div className="flex gap-2">
                      <button onClick={() => saveProduct(p)} disabled={!dirty} data-testid={`admin-save-${p.id}`}
                        className="flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-[11px] font-semibold text-brandink transition-colors hover:border-link hover:text-link disabled:opacity-30">
                        <Save className="h-3.5 w-3.5" /> Save
                      </button>
                      <button onClick={() => deleteProduct(p)} data-testid={`admin-delete-${p.id}`}
                        className="flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-[11px] font-semibold text-muted transition-colors hover:border-red-400 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {products.length > adminVisible && (
              <button
                data-testid="admin-products-more"
                onClick={() => setAdminVisible((v) => v + 50)}
                className="mt-3 w-full rounded-lg border border-line bg-white py-2.5 text-xs font-bold text-brandink transition-colors hover:border-brandink"
              >
                Show more ({products.length - adminVisible} remaining)
              </button>
            )}
          </div>
        )}

        {tab === "categories" && (
          <div className="mt-5" data-testid="admin-categories-list">
            <p className="mb-3 text-xs text-muted">
              Homepage banner: up to 3 slides rotating every 5 seconds. Each category: one tile photo (homepage row and menu) plus up to 3 hero banners (top of the category page, rotating every 5 seconds).
            </p>

            <div className="mb-4 rounded-lg border border-line bg-white p-3" data-testid="admin-home-hero">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-bold text-brandink">
                  Homepage banner <span className="text-muted">({homeSlides.length}/3)</span>
                </p>
                {homeSlides.length < 3 && (
                  <button
                    data-testid="admin-home-hero-upload"
                    onClick={() => { setHeroFor(null); setTileFor(null); setGalleryFor(null); setHomeHeroPick(true); fileRef.current?.click(); }}
                    className="flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-[11px] font-semibold text-brandink transition-colors hover:border-link hover:text-link"
                  >
                    <ImagePlus className="h-3.5 w-3.5" /> Add banner
                  </button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {homeSlides.map((u, i) => (
                  <span key={u} className="relative">
                    <img src={u} alt={`Homepage banner ${i + 1}`} className="h-14 w-24 rounded-md border border-line object-cover" data-testid={`admin-home-hero-slide-${i}`} />
                    <button
                      data-testid={`admin-home-hero-remove-${i}`}
                      onClick={() => removeHomeSlide(u)}
                      aria-label="Remove banner"
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {CATEGORIES.map((c) => {
                const custom = catHeros[c.id] || [];
                const heroes = custom.length ? custom : [catTiles[c.id] || c.image];
                return (
                  <div key={c.id} data-testid={`admin-category-${c.id}`} className="rounded-lg border border-line bg-white p-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={catTiles[c.id] || c.image}
                        alt={`${c.name} tile`}
                        className="h-14 w-14 shrink-0 rounded-full border border-line object-cover"
                        data-testid={`admin-category-tile-${c.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-brandink">{c.name}</p>
                        <p className="truncate text-[10px] text-muted">{c.name_ar}</p>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1.5">
                        <button
                          data-testid={`admin-tile-upload-${c.id}`}
                          onClick={() => { setHeroFor(null); setHomeHeroPick(false); setGalleryFor(null); setTileFor(c.id); fileRef.current?.click(); }}
                          className="flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-[11px] font-semibold text-brandink transition-colors hover:border-link hover:text-link"
                        >
                          <ImagePlus className="h-3.5 w-3.5" /> Photo
                        </button>
                        <button
                          data-testid={`admin-hero-upload-${c.id}`}
                          disabled={custom.length >= 3}
                          onClick={() => { setTileFor(null); setHomeHeroPick(false); setGalleryFor(null); setHeroFor(c.id); fileRef.current?.click(); }}
                          className="flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-[11px] font-semibold text-brandink transition-colors hover:border-link hover:text-link disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <ImagePlus className="h-3.5 w-3.5" /> Hero ({custom.length}/3)
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-category-hero-${c.id}`}>
                      {heroes.map((u, i) => (
                        <span key={u} className="relative">
                          <img src={u} alt={`${c.name} banner ${i + 1}`} className="h-14 w-20 rounded-md border border-line object-cover" />
                          {custom.length > 0 && (
                            <button
                              data-testid={`admin-hero-remove-${c.id}-${i}`}
                              onClick={() => removeCatHero(c.id, u)}
                              aria-label="Remove banner"
                              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-5 space-y-2" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="rounded-lg border border-line bg-white py-16 text-center text-sm text-muted">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.order_id} data-testid={`admin-order-${o.order_id}`}
                className="grid grid-cols-[110px_1fr_120px_130px_150px] items-center gap-3 rounded-lg border border-line bg-white p-4 text-sm max-md:grid-cols-1">
                <span className="font-mono text-xs font-bold tracking-wider text-brandink">{o.order_id}</span>
                <div>
                  <p className="font-medium text-brandink">{o.customer?.name} · {o.customer?.emirate}</p>
                  <p className="text-xs text-muted">
                    {o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}
                  </p>
                  <p className="mt-1 text-[10px] text-muted">
                    {o.payment_method === "cod" ? "Cash on Delivery" : o.payment_method === "bank_transfer" ? "Bank Transfer" : "Card (Stripe)"}
                    {" · "}{o.payment_status}
                  </p>
                </div>
                <span className="font-extrabold text-brandink">AED {o.total?.toLocaleString()}</span>
                <span className="text-xs text-muted">{new Date(o.created_at).toLocaleDateString("en-GB")}</span>
                <select
                  data-testid={`admin-order-status-${o.order_id}`}
                  value={o.status}
                  onChange={(e) => updateOrderStatus(o.order_id, e.target.value)}
                  className={`${inputCls} cursor-pointer text-xs`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        {galleryFor && (
          <div data-testid="admin-gallery-modal" className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="text-base font-bold text-brandink">Photos — {galleryFor.name}</h3>
                <span className="rounded-full bg-page px-2.5 py-1 text-[10px] font-bold text-muted">{galleryImages.length}/10</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {galleryImages.map((u, i) => (
                  <div key={i} className="relative" data-testid={`gallery-slot-${i}`}>
                    <img src={u} alt="" className={`h-20 w-full rounded-md border object-contain ${i === 0 ? "border-brand" : "border-line"}`} />
                    {i === 0 && (
                      <span className="absolute bottom-1 start-1 rounded bg-brand px-1 text-[8px] font-bold uppercase text-brandink">Cover</span>
                    )}
                    <button
                      data-testid={`gallery-remove-${i}`}
                      onClick={() => setGalleryImages((imgs) => imgs.filter((_, j) => j !== i))}
                      className="absolute -end-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] text-white"
                      aria-label="Remove photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {galleryImages.length < 10 && (
                  <button
                    data-testid="gallery-upload"
                    onClick={() => fileRef.current?.click()}
                    className="flex h-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-line text-muted transition-colors hover:border-link hover:text-link"
                  >
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-[9px] font-semibold uppercase tracking-wide">Add</span>
                  </button>
                )}
              </div>
              {photoBusy && (
                <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-link">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…
                </p>
              )}
              <p className="mt-3 text-[10px] text-muted">First photo is the cover shown in the shop. Up to 10 photos.</p>
              <div className="mt-5 flex gap-3">
                <button
                  data-testid="gallery-close"
                  onClick={() => setGalleryFor(null)}
                  className="rounded-md border border-line px-5 py-2.5 text-xs font-semibold text-brandink transition-colors hover:bg-page"
                >
                  Cancel
                </button>
                <button
                  data-testid="gallery-save"
                  onClick={saveGallery}
                  className="flex-1 rounded-md bg-brand py-2.5 text-xs font-bold text-brandink transition-colors hover:bg-branddark"
                >
                  Save Photos
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
